import React, { useEffect, useState } from "react";
import { api, formatDate } from "../../lib/api";
import { Megaphone, Send, Save, Trash2, Info } from "lucide-react";
import { toast } from "sonner";

export default function BroadcastsTab() {
  const [list, setList] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/broadcasts");
      setList(data);
    } catch {
      toast.error("Failed to load broadcasts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveDraft = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/broadcasts", { subject, body });
      toast.success("Draft saved");
      setSubject("");
      setBody("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const sendOne = async (b) => {
    if (!confirm(`Send "${b.subject}" to all subscribers now?\n\nThis will deliver via Resend from hello@thegivingstitch.com. Every email includes an Unsubscribe link.`))
      return;
    try {
      const { data } = await api.post(`/admin/broadcasts/${b.id}/send`);
      const sent = data.sent_count ?? data.queued_for ?? 0;
      const failed = data.failed_count ?? 0;
      if (failed > 0) {
        toast.warning(`Sent to ${sent} subscribers · ${failed} failed`);
      } else if (sent === 0) {
        toast.info("No active subscribers — nothing was sent.");
      } else {
        toast.success(`Sent to ${sent} subscriber${sent === 1 ? "" : "s"}`);
      }
      await load();
    } catch {
      toast.error("Send failed");
    }
  };

  const remove = async (b) => {
    if (!confirm(`Delete this broadcast?`)) return;
    try {
      await api.delete(`/admin/broadcasts/${b.id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="broadcasts-tab">
      <header className="mb-8">
        <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
          stay in touch
        </p>
        <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
          Email Blasts
        </h1>
      </header>

      <div
        className="rounded-xl px-4 py-3 mb-6 flex items-start gap-3 text-sm"
        style={{ backgroundColor: "rgba(74,93,78,0.10)", color: "var(--text-primary)" }}
      >
        <Info size={18} style={{ color: "var(--brand-sage)", flexShrink: 0, marginTop: 1 }} />
        <p>
          Email delivery is live via Resend — blasts send from{" "}
          <strong>hello@thegivingstitch.com</strong>, replies route to your inbox, and every email
          includes a one-click Unsubscribe link.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div
          className="lg:col-span-2 bg-white rounded-2xl p-6"
          style={{ border: "1px solid var(--border-soft)" }}
        >
          <h2 className="font-display text-2xl mb-4" style={{ color: "var(--text-primary)" }}>
            New broadcast
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold block mb-1.5">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl outline-none"
                style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
                placeholder="A new batch just landed 🧶"
                data-testid="broadcast-subject-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">Body</label>
              <textarea
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl outline-none resize-none"
                style={{ backgroundColor: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
                placeholder="Hi friend, just dropped 5 new floppy-ear bunnies…"
                data-testid="broadcast-body-input"
              />
            </div>
            <button
              onClick={saveDraft}
              disabled={busy}
              className="btn-sage w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
              data-testid="save-broadcast-button"
            >
              <Save size={16} />
              {busy ? "Saving…" : "Save Draft"}
            </button>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-3">
          <h2 className="font-display text-2xl mb-2" style={{ color: "var(--text-primary)" }}>
            History
          </h2>
          {loading ? (
            <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
              loading…
            </p>
          ) : list.length === 0 ? (
            <p style={{ color: "var(--text-secondary)" }}>No broadcasts yet — compose one to the left.</p>
          ) : (
            list.map((b) => (
              <article
                key={b.id}
                className="bg-white rounded-2xl p-5"
                style={{ border: "1px solid var(--border-soft)" }}
                data-testid={`broadcast-${b.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Megaphone size={16} style={{ color: "var(--brand-sage)" }} />
                      <h3 className="font-display text-lg" style={{ color: "var(--text-primary)" }}>
                        {b.subject}
                      </h3>
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: b.status === "queued" || b.status === "sent" ? "rgba(74,93,78,0.12)" : "rgba(160,82,45,0.1)",
                          color: b.status === "queued" || b.status === "sent" ? "var(--brand-sage)" : "var(--text-accent)",
                        }}
                      >
                        {b.status}
                      </span>
                    </div>
                    <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>
                      {b.body.length > 240 ? `${b.body.slice(0, 240)}…` : b.body}
                    </p>
                    <p className="text-xs mt-3" style={{ color: "var(--text-secondary)" }}>
                      Created {formatDate(b.created_at)}
                      {b.sent_at && ` · Queued ${formatDate(b.sent_at)} for ${b.sent_count} subscribers`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  {b.status === "draft" && (
                    <button
                      onClick={() => sendOne(b)}
                      className="btn-sage inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
                      data-testid={`send-broadcast-${b.id}`}
                    >
                      <Send size={12} />
                      Send to all
                    </button>
                  )}
                  <button
                    onClick={() => remove(b)}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold"
                    style={{ border: "1px solid var(--border-soft)", color: "var(--brand-orange)" }}
                    data-testid={`delete-broadcast-${b.id}`}
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
